import { useState, useCallback } from 'react'
import { generatePropReference } from '../../../services/api/aiVideoGeneration'
import { useTaskPoller } from './useTaskPoller'

const POLL_INTERVAL = 15000

/**
 * Custom hook to manage prop reference generation and polling
 * Handles prop map generation, editing, and batch updates
 */
export const usePropReferences = ({ formData, setFormData, videoId } = {}) => {
  const {
    isPolling,
    progress: propGenerationProgress,
    status: propGenerationStatus,
    startPolling
  } = useTaskPoller({ pollInterval: POLL_INTERVAL })

  const [isRequesting, setIsRequesting] = useState(false)

  /**
   * Generate prop map references
   */
  const generatePropMap = useCallback(
    async (propMapObj, existingUris) => {
      setIsRequesting(true)
      try {
        const propRes = await generatePropReference({
          prop_map_with_prompts: propMapObj,
          existing_prop_uris:
            existingUris !== undefined
              ? existingUris
              : formData?.props_reference,
          video_id: videoId
        })
        const taskId = propRes && propRes.data && propRes.data.task_id
        if (taskId) {
          setIsRequesting(false)
          const result = await startPolling(taskId)
          if (result && result.status === 'success' && result.output) {
            setFormData(prev => ({
              ...prev,
              props_reference: result.output
            }))
          }
        }
      } finally {
        setIsRequesting(false)
      }
    },
    [setFormData, startPolling, videoId]
  )

  /**
   * Add new prop reference
   */
  const handleAddPropReference = useCallback(
    async newPropMap => {
      try {
        const propRes = await generatePropReference({
          prop_map_with_prompts: {
            ...(formData?.propMap || {}),
            ...newPropMap
          },
          existing_prop_uris: formData?.props_reference,
          video_id: videoId
        })
        const taskId = propRes && propRes.data && propRes.data.task_id
        if (taskId) {
          const result = await startPolling(taskId)
          if (result && result.status === 'success' && result.output) {
            setFormData(prev => ({
              ...prev,
              props_reference: {
                ...(prev.props_reference || {}),
                ...result.output
              },
              propMap: {
                ...(prev.propMap || {}),
                ...newPropMap
              }
            }))
          }
        }
      } catch (error) {
        console.error('Error adding prop reference:', error)
      }
    },
    [
      formData?.propMap,
      formData?.props_reference,
      setFormData,
      startPolling,
      videoId
    ]
  )

  /**
   * Edit existing prop reference
   */
  const handleEditPropReference = useCallback(
    async editingPropPrompt => {
      try {
        const editingKeys = Object.keys(editingPropPrompt)

        // Remove old URIs for edited props
        const filteredPropUris = Object.keys(
          formData?.props_reference || {}
        ).reduce((acc, key) => {
          if (!editingKeys.includes(key)) {
            acc[key] = formData.props_reference[key]
          }
          return acc
        }, {})

        const propRes = await generatePropReference({
          prop_map_with_prompts: {
            ...(formData?.propMap || {}),
            ...editingPropPrompt
          },
          existing_prop_uris: filteredPropUris,
          video_id: videoId
        })

        const taskId = propRes?.data?.task_id

        if (taskId) {
          const result = await startPolling(taskId)
          if (result && result.status === 'success' && result.output) {
            setFormData(prev => ({
              ...prev,
              props_reference: {
                ...(prev.props_reference || {}),
                ...result.output
              },
              propMap: {
                ...(prev.propMap || {}),
                ...editingPropPrompt
              }
            }))
          }
        }
      } catch (error) {
        console.error('Error editing prop reference:', error)
      }
    },
    [
      formData?.propMap,
      formData?.props_reference,
      setFormData,
      startPolling,
      videoId
    ]
  )

  /**
   * Batch update multiple props
   */
  const handleBatchUpdateProps = useCallback(
    async (updatedPropMap, modifiedKeys) => {
      try {
        // Filter out URIs for modified keys
        const filteredPropUris = Object.keys(
          formData?.props_reference || {}
        ).reduce((acc, key) => {
          if (!modifiedKeys.includes(key)) {
            acc[key] = formData.props_reference[key]
          }
          return acc
        }, {})

        const propRes = await generatePropReference({
          prop_map_with_prompts: updatedPropMap,
          existing_prop_uris: filteredPropUris,
          video_id: videoId
        })

        const taskId = propRes?.data?.task_id

        if (taskId) {
          const result = await startPolling(taskId)
          if (result && result.status === 'success' && result.output) {
            setFormData(prev => ({
              ...prev,
              props_reference: {
                ...(prev.props_reference || {}),
                ...result.output
              },
              propMap: {
                ...(prev.propMap || {}),
                ...updatedPropMap
              }
            }))
          }
        }
      } catch (error) {
        console.error('Error batch updating prop references:', error)
      }
    },
    [formData?.props_reference, setFormData, startPolling, videoId]
  )

  /**
   * Reset prop generation state
   */
  const resetPropGeneration = useCallback(() => {
    // Note: useTaskPoller handles its own state, but we provide this for compatibility
  }, [])

  return {
    // State
    isGeneratingPropReference: isPolling || isRequesting,
    propGenerationProgress,
    propGenerationStatus,

    // Actions
    generatePropMap,
    handleAddPropReference,
    handleEditPropReference,
    handleBatchUpdateProps,
    resetPropGeneration
  }
}

export default usePropReferences
